import { ChevronRight } from "lucide-react";
import React, { useMemo } from "react";

import { IgButton, IgTypography } from "@intelligems/ig-theme";
import {
  MESSAGE_BOX_VARIANT_NAMES,
  MessageBoxVariant,
  MessageBoxWidgetDto,
  PROGRESS_BAR_VARIANT_NAMES,
  ProgressBarWidgetDto,
  ProgressBarWidgetVariant,
  QUANTITY_BUTTONS_VARIANT_NAMES,
  QuantityButtonsVariant,
  QuantityButtonsWidgetDto,
} from "@intelligems/ig-types";
import { Grid, useTheme } from "@mui/material";
import { ComponentStyleVariantSelectionDrawer } from "@src/components/ColorAndStyle/sections/ComponentStyleVariantSelectionDrawer";
import { useDispatch } from "@src/store";
import { openDrawer } from "@src/store/slices/drawers";
import { isMessageBox, isProgressBar } from "@src/store/slices/widgets/util";

interface ComponentStyleVariantSelectionProps {
  widget: ProgressBarWidgetDto | QuantityButtonsWidgetDto | MessageBoxWidgetDto;
  interfaceId: string;
}

const ComponentVariantNameMap = {
  ...PROGRESS_BAR_VARIANT_NAMES,
  ...QUANTITY_BUTTONS_VARIANT_NAMES,
  ...MESSAGE_BOX_VARIANT_NAMES,
};

export const ComponentStyleVariantSelection = ({
  widget,
  interfaceId,
}: ComponentStyleVariantSelectionProps) => {
  const theme = useTheme();
  const isProgressBarWidget = isProgressBar(widget);
  const isMessageBoxWidget = isMessageBox(widget);
  const dispatch = useDispatch();
  const variantList: {
    label: string;
    description: string;
    value:
      | MessageBoxVariant
      | QuantityButtonsVariant
      | ProgressBarWidgetVariant;
  }[] = useMemo(() => {
    if (widget.widgetType === "quantityButtons") {
      return [
        {
          label: QUANTITY_BUTTONS_VARIANT_NAMES.quantityButtonsWidgetClassic,
          description: "Clean, vertical layout with quantity tiers",
          value: "quantityButtonsWidgetClassic",
        },
        {
          label: QUANTITY_BUTTONS_VARIANT_NAMES.quantityButtonsWidgetImage,
          description: "Includes product images for each option",
          value: "quantityButtonsWidgetImage",
        },
        {
          label: QUANTITY_BUTTONS_VARIANT_NAMES.quantityButtonsWidgetCompact,
          description: "Space-saving layout for quick compare",
          value: "quantityButtonsWidgetCompact",
        },
      ];
    } else if (
      widget.widgetType === "shippingProgressBar" ||
      widget.widgetType === "discountProgressBar"
    ) {
      return [
        {
          label: PROGRESS_BAR_VARIANT_NAMES.progressBar1,
          description: "Best for multi-tier setups",
          value: "progressBar1",
        },
        {
          label: PROGRESS_BAR_VARIANT_NAMES.progressBar2,
          description: "Compact layouts with 2–3 tiers",
          value: "progressBar2",
        },
        {
          label: PROGRESS_BAR_VARIANT_NAMES.progressBar3,
          description: "For single-tier setups like free shipping",
          value: "progressBar3",
        },
      ];
    } else {
      return [
        {
          label: MESSAGE_BOX_VARIANT_NAMES.messageBoxPopupWidget1,
          description: "Standard promotional call-out",
          value: "messageBoxPopupWidget1",
        },
        {
          label: MESSAGE_BOX_VARIANT_NAMES.messageBoxSlideOutWidget1,
          description: "Promotional call-out with slide animation",
          value: "messageBoxSlideOutWidget1",
        },
      ];
    }
  }, [widget.widgetType]);

  return (
    <Grid item container direction="column" columnSpacing={2}>
      <ComponentStyleVariantSelectionDrawer
        variantsList={variantList}
        interfaceId={interfaceId}
        widget={widget}
      />
      <Grid item>
        <IgTypography fontWeight="600" variant={"body1"}>
          {isProgressBarWidget
            ? "Bar"
            : isMessageBoxWidget
              ? "Offer message"
              : "Button"}{" "}
          style
        </IgTypography>
      </Grid>
      <Grid item>
        <Grid
          container
          alignItems="center"
          justifyContent="flex-start"
          spacing={7}
        >
          <Grid item>
            <IgTypography>
              {
                ComponentVariantNameMap[
                  widget.config?.desktop?.variant ??
                    "quantityButtonsWidgetCompact"
                ]
              }
            </IgTypography>
          </Grid>
          <Grid item>
            <IgButton
              onClick={() => {
                dispatch(
                  openDrawer({ type: "componentStyleVariantSelection" })
                );
              }}
              color={"link"}
              endIcon={
                <ChevronRight color={theme.igIconColors.accent} size={16} />
              }
            >
              Change{" "}
              {isProgressBarWidget
                ? "bar"
                : isMessageBoxWidget
                  ? "message"
                  : "button"}{" "}
              style
            </IgButton>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};
